/**
 * Universal Schema Table Component
 * Works with any OpenAPI specification to generate tables
 */

import { useState, useEffect } from 'react';
import { Table, Badge, Spinner, DropdownButton, Dropdown } from 'react-bootstrap';
import { parseOpenAPISchemaToColumns, TableColumnConfig } from '../../utils/universalTableParser';

export interface UniversalSchemaTableProps {
  openApiSpec: any;                    // The OpenAPI specification
  schemaName: string;                  // Which schema to use for the table
  data: any[];                         // Table data
  loading?: boolean;                   // Show loading state
  emptyMessage?: string;               // Message when no data
  onEdit?: (item: any) => void;        // Edit action
  onDelete?: (item: any) => void;      // Delete action
  onView?: (item: any) => void;        // View action
  customActions?: Array<{             // Custom action buttons
    label: string;
    icon: string;
    variant?: string;
    onClick: (item: any) => void;
    condition?: (item: any) => boolean;
  }>;
  
  // Customization options
  hiddenFields?: string[];             // Fields to hide
  customLabels?: Record<string, string>;        // Override column labels
  customColumnTypes?: Record<string, TableColumnConfig['type']>; // Override column types
  
  // Table options
  striped?: boolean;
  hover?: boolean;
  responsive?: boolean;
  size?: 'sm' | 'lg';
}

export function UniversalSchemaTable({
  openApiSpec,
  schemaName,
  data,
  loading = false,
  emptyMessage = 'No data found',
  onEdit,
  onDelete,
  onView,
  customActions = [],
  hiddenFields = [],
  customLabels = {},
  customColumnTypes = {},
  striped = true,
  hover = true,
  responsive = true,
  size
}: UniversalSchemaTableProps): JSX.Element {
  const [columnConfigs, setColumnConfigs] = useState<TableColumnConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate column configurations from OpenAPI schema
  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);

      const configs = parseOpenAPISchemaToColumns(
        openApiSpec, 
        schemaName, 
        hiddenFields,
        customLabels,
        customColumnTypes
      );
      
      if (configs.length === 0) {
        throw new Error(`No columns found for schema: ${schemaName}`);
      }

      setColumnConfigs(configs);
      
    } catch (err) {
      console.error('❌ Error generating table columns:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate table columns');
    } finally {
      setIsLoading(false);
    }
  }, [openApiSpec, schemaName, JSON.stringify(hiddenFields)]);

  // Render cell content based on column type
  const renderCellContent = (config: TableColumnConfig, value: any, row: any) => {
    if (value === null || value === undefined) {
      return <span className="text-muted">-</span>;
    }

    switch (config.type) {
      case 'badge':
        const variant = config.badgeVariant ? config.badgeVariant(value) : 'secondary';
        return (
          <Badge bg={variant}>
            {value}
          </Badge>
        );

      case 'status':
        const statusVariant = config.badgeVariant ? config.badgeVariant(value) : 'secondary';
        return (
          <Badge bg={statusVariant}>
            {value}
          </Badge>
        );

      case 'date':
        try {
          const date = new Date(value);
          return isNaN(date.getTime()) ? <span className="text-muted">-</span> : date.toLocaleDateString();
        } catch {
          return <span className="text-muted">-</span>;
        }

      case 'datetime':
        try {
          const date = new Date(value);
          return isNaN(date.getTime()) ? <span className="text-muted">-</span> : date.toLocaleDateString();
        } catch {
          return <span className="text-muted">-</span>;
        }

      case 'email':
        return (
          <a href={`mailto:${value}`} className="text-decoration-none">
            {value}
          </a>
        );

      case 'phone':
        return (
          <a href={`tel:${value}`} className="text-decoration-none">
            {value}
          </a>
        );

      case 'text':
      default:
        return value;
    }
  };

  // Render actions dropdown
  const renderActions = (row: any) => {
    const actions: Array<{
      label: string;
      icon: string;
      variant?: string;
      onClick: () => void;
    }> = [];
    
    if (onView) {
      actions.push({
        label: 'View',
        icon: 'fas fa-eye',
        onClick: () => onView(row)
      });
    }
    
    if (onEdit) {
      actions.push({
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: () => onEdit(row)
      });
    }
    
    // Add custom actions
    customActions.forEach(action => {
      if (!action.condition || action.condition(row)) {
        actions.push({
          label: action.label,
          icon: action.icon,
          variant: action.variant,
          onClick: () => action.onClick(row)
        });
      }
    });
    
    if (onDelete) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        variant: 'danger',
        onClick: () => onDelete(row)
      });
    }

    if (actions.length === 0) {
      return null;
    }

    const primaryKey = row.id || row.certificateId || row.organizationId || row.productId || 'unknown';

    return (
      <DropdownButton
        id={`actions-${primaryKey}`}
        title="Actions"
        size="sm"
        variant="outline-secondary"
      >
        {actions.map((action, index) => (
          <Dropdown.Item 
            key={index}
            onClick={action.onClick}
            className={action.variant === 'danger' ? 'text-danger' : ''}
          >
            <i className={`${action.icon} me-2`}></i>
            {action.label}
          </Dropdown.Item>
        ))}
      </DropdownButton>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">Generating table from schema...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h6 className="alert-heading">Table Generation Error</h6>
        <p className="mb-0">{error}</p>
        <hr />
        <small className="text-muted">
          Please check that the schema name "{schemaName}" exists in the OpenAPI specification.
        </small>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading data...</p>
      </div>
    );
  }

  // Determine if we need an actions column
  const hasActions = onEdit || onDelete || onView || customActions.length > 0;
  const totalColumns = columnConfigs.length + (hasActions ? 1 : 0);

  return (
    <Table responsive={responsive} striped={striped} hover={hover} size={size}>
      <thead>
        <tr>
          {columnConfigs.map(config => (
            <th key={config.key} className={config.align ? `text-${config.align}` : ''}>
              {config.label}
            </th>
          ))}
          {hasActions && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan={totalColumns} className="text-center text-muted py-4">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          data.map((row, index) => {
            const primaryKey = row.id || row.certificateId || row.organizationId || row.productId || index;
            return (
              <tr key={primaryKey}>
                {columnConfigs.map(config => (
                  <td key={config.key} className={config.align ? `text-${config.align}` : ''}>
                    {renderCellContent(config, row[config.key], row)}
                  </td>
                ))}
                {hasActions && (
                  <td>
                    {renderActions(row)}
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </Table>
  );
}